import {
  type NotificationDeliveryListInput,
  type NotificationDeliveryStatus,
  type NotificationKind,
  notificationRecipientEmailSchema,
} from '@repo/api-contracts';
import { koreanDate } from '@/shared/lib';

/**
 * 발송 이력 화면의 거르기 값. 기간은 한국 달력 날짜('YYYY-MM-DD', 비면 '')다. 수신 이메일은 검색으로 적용한 값(소문자)이고,
 * 적용하지 않았으면 null 이다.
 */
export interface DeliveryFilters {
  readonly kind: NotificationKind | null;
  readonly status: NotificationDeliveryStatus | null;
  readonly fromDate: string;
  readonly toDate: string;
  readonly recipientEmail: string | null;
}

export const EMPTY_DELIVERY_FILTERS: DeliveryFilters = {
  kind: null,
  status: null,
  fromDate: '',
  toDate: '',
  recipientEmail: null,
};

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/** 한국 날짜 0시의 epoch ms. KST 는 UTC+9 이고 일광 절약 시간이 없다. 날짜 형식이 아니면 null 이다. */
const kstMidnightMs = (date: string): number | null => {
  if (!CALENDAR_DATE.test(date)) return null;
  const ms = Date.parse(`${date}T00:00:00+09:00`);
  return Number.isNaN(ms) ? null : ms;
};

/** 그 한국 날짜 하루의 시작 — 계약 `from`(이상 경계, Z 로 끝나는 ISO). */
export const kstDayStart = (date: string): string | null => {
  const ms = kstMidnightMs(date);
  return ms === null ? null : new Date(ms).toISOString();
};

/** 그 한국 날짜 다음 날의 시작. 계약 `to` 는 `created_at < to`(미만 경계)라서 이 값을 보내야 종료일 하루가 다 들어간다. */
export const kstNextDayStart = (date: string): string | null => {
  const ms = kstMidnightMs(date);
  return ms === null ? null : new Date(ms + DAY_MS).toISOString();
};

/** 화면 거르기 값 → 목록 입력(listDeliveries). 빈 조건은 칸을 뺀다. 계약 스키마가 빈 수신 이메일을 거부하기 때문이다. */
export const deliveryQueryOf = (
  filters: DeliveryFilters,
  page: number,
  limit: number,
): NotificationDeliveryListInput => {
  const from = kstDayStart(filters.fromDate);
  const to = kstNextDayStart(filters.toDate);

  return {
    page,
    limit,
    ...(filters.kind === null ? {} : { kind: filters.kind }),
    ...(filters.status === null ? {} : { status: filters.status }),
    ...(from === null ? {} : { from }),
    ...(to === null ? {} : { to }),
    ...(filters.recipientEmail === null ? {} : { recipientEmail: filters.recipientEmail }),
  };
};

/** 목록 입력 → 화면 거르기 값(처음 조건을 받을 때). `to` 는 미만 경계라 1ms 앞의 한국 날짜가 종료일이다. */
export const filtersOfQuery = (query: NotificationDeliveryListInput): DeliveryFilters => ({
  kind: query.kind ?? null,
  status: query.status ?? null,
  fromDate: query.from === undefined ? '' : koreanDate(query.from),
  toDate: query.to === undefined ? '' : koreanDate(new Date(Date.parse(query.to) - 1)),
  recipientEmail: query.recipientEmail ?? null,
});

export type RecipientEmailInput =
  | { readonly kind: 'EMPTY' }
  | { readonly kind: 'VALID'; readonly email: string }
  | { readonly kind: 'INVALID' };

/** 수신 이메일 검색칸 값. 비었는지, 계약 규칙(trim·소문자·이메일 형식)에 맞는지 가른다. */
export const parseRecipientEmail = (value: string): RecipientEmailInput => {
  if (value.trim() === '') return { kind: 'EMPTY' };
  const parsed = notificationRecipientEmailSchema.safeParse(value);
  return parsed.success ? { kind: 'VALID', email: parsed.data } : { kind: 'INVALID' };
};

/** 기간이 거꾸로인지(시작일 > 종료일) — 둘 다 있을 때만 본다. 'YYYY-MM-DD' 는 글자 순서가 날짜 순서다. */
export const isDateRangeReversed = (filters: DeliveryFilters): boolean =>
  filters.fromDate !== '' && filters.toDate !== '' && filters.fromDate > filters.toDate;

/**
 * 재발송 뒤의 거르기 값 — 새 기록은 "대기"라서 결과 필터가 대기가 아닌 값이면 새 행이 숨는다. 그때는 결과 필터만 푼다.
 * 나머지 조건(종류·기간·이메일)은 그대로 둔다.
 */
export const filtersAfterResend = (filters: DeliveryFilters): DeliveryFilters =>
  filters.status === null || filters.status === 'QUEUED' ? filters : { ...filters, status: null };
